import {Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {forkJoin, Observable, of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {TranslationService} from '../../../core/services/translation.service';
import {NavigationCommand, NavigationService} from '../../../data/services/navigation.service';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {HupSubscription} from '../../../data/models/subscription.model';

/**
 * Bestätigungsseite nach erfolgreicher Zubuchung (P5W-4471, P5W-4460).
 *
 * Route: /additional-booking/ordered/:orderIds - `orderIds` sind die kommagetrennten BackendIds der
 * NEU angelegten Kombi-Abos (nicht die des Hauptabonnements). Wurden mehrere Angebote in einem
 * Schritt gebucht ("Alle buchen"), werden alle aufgeführt.
 *
 * Die Abos werden bewusst über die Route nachgeladen statt im Navigationszustand mitgegeben:
 * so übersteht die Seite ein Neuladen, und `getSubscription` liefert jedes Abo durch
 * `handleSubscription` angereichert (Produktbezeichnung, Ausgabe, Auftragsnummer, Zeitraum).
 * Dasselbe Muster nutzt die Bestellbestätigung OfferOrderedOverviewComponent.
 */
@Component({
  selector: 'app-additional-booking-ordered',
  templateUrl: './additional-booking-ordered.component.html',
  styleUrls: ['./additional-booking-ordered.component.scss']
})
export class AdditionalBookingOrderedComponent implements OnInit, OnDestroy {

  /**
   * Kombi-Abos werden direkt in Paradise angelegt und liegen nie in der Web-DB; die webId ist
   * daher immer 0. pca fällt bei fehlendem Web-DB-Treffer auf Paradise zurück.
   */
  private static readonly WEB_ID_PARADISE_ONLY = '0';

  /**
   * Schlüssel im History-State, unter dem die Bestätigungsseite die Bezeichnungen der NICHT
   * zugebuchten Angebote mitgibt. Bewusst im History-State statt in der Route: die Bezeichnungen
   * gehören nicht in die URL, überstehen dort aber trotzdem ein Neuladen der Seite.
   */
  public static readonly NOT_BOOKED_OFFERS_STATE_KEY = 'notBookedOffers';
  /**
   * Schlüssel im History-State für die ANZAHL der nicht zugebuchten Angebote. Sie trägt den Hinweis
   * auch dann, wenn sich die Angebote nicht benennen ließen und die Namensliste leer ist.
   */
  public static readonly NOT_BOOKED_COUNT_STATE_KEY = 'notBookedCount';

  /** Hinweistext, falls 'additionalBooking_ordered_notBooked' für den Mandanten nicht gepflegt ist. */
  private static readonly NOT_BOOKED_FALLBACK_HINT =
    'Folgende Angebote konnten nicht zugebucht werden. Bitte wenden Sie sich an den Verlag.';

  protected orders: HupSubscription[] = [];
  /** Bezeichnungen der gewählten Angebote, deren Zubuchung gescheitert ist. */
  protected notBookedOffers: string[] = [];
  /** Anzahl der gewählten Angebote, deren Zubuchung gescheitert ist - auch ohne bekannte Namen. */
  protected notBookedCount: number = 0;
  protected errors: string[] = [];
  protected isLoading: boolean = true;

  private paramSub: Subscription;
  private loadSub: Subscription;

  constructor(public translationService: TranslationService,
              private route: ActivatedRoute,
              private navigationService: NavigationService,
              private subscriptionService: HupSubscriptionsService,
              @Inject(PLATFORM_ID) private platformId: Object) {
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // Beim Server-Rendering liegt kein SSO-Token vor; die Abos wären nicht ladbar.
      return;
    }
    this.readNotBooked(window.history.state);
    this.paramSub = this.route.paramMap.subscribe(params => {
      this.loadSubscriptions(params.get('orderIds'));
    });
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.paramSub?.unsubscribe();
  }

  private loadSubscriptions(orderIdsParam: string): void {
    this.isLoading = true;
    this.orders = [];
    this.errors = [];
    this.loadSub?.unsubscribe();

    const orderIds = (orderIdsParam ?? '').split(',').map(id => id.trim()).filter(id => !!id);
    if (orderIds.length === 0) {
      console.error('Bestätigung der Zubuchung ohne Auftragsnummer aufgerufen');
      this.handleLoadError();
      return;
    }

    // Ein einzelnes nicht ladbares Abo soll die übrigen nicht verdecken - es fehlt nur in der Liste.
    this.loadSub = forkJoin(orderIds.map(orderId => this.loadSubscription(orderId))).subscribe(loaded => {
      this.orders = loaded.filter(order => !!order);
      if (this.orders.length === 0) {
        this.handleLoadError();
        return;
      }
      this.isLoading = false;
    });
  }

  private loadSubscription(orderId: string): Observable<HupSubscription> {
    return this.subscriptionService
      .getSubscription(AdditionalBookingOrderedComponent.WEB_ID_PARADISE_ONLY, orderId)
      .pipe(catchError(error => {
        console.error(`Zugebuchtes Abonnement ${orderId} konnte nicht geladen werden`, error);
        return of(null);
      }));
  }

  private handleLoadError(): void {
    this.errors.push(this.translationService.getTranslation('error_subscription_init'));
    this.isLoading = false;
  }

  /**
   * Liest die nicht zugebuchten Angebote aus dem History-State; was nicht die erwartete Form hat,
   * wird ignoriert. Die Anzahl ist nie kleiner als die Zahl der genannten Angebote.
   */
  private readNotBooked(state: unknown): void {
    const values = (state ?? {}) as Record<string, unknown>;
    const offers = values[AdditionalBookingOrderedComponent.NOT_BOOKED_OFFERS_STATE_KEY];
    this.notBookedOffers = Array.isArray(offers)
      ? offers.filter((offer): offer is string => typeof offer === 'string' && offer.trim().length > 0)
      : [];
    const count = values[AdditionalBookingOrderedComponent.NOT_BOOKED_COUNT_STATE_KEY];
    this.notBookedCount = Math.max(typeof count === 'number' && count > 0 ? count : 0, this.notBookedOffers.length);
  }

  /** Hinweis über den nicht zugebuchten Angeboten: nicht angelegt, bitte an den Verlag wenden. */
  public get notBookedHint(): string {
    return this.translationService.getTranslation('additionalBooking_ordered_notBooked')
      || AdditionalBookingOrderedComponent.NOT_BOOKED_FALLBACK_HINT;
  }

  /** Beschreibungstext für ein bzw. mehrere zugebuchte Produkte. */
  public get descriptionKey(): string {
    return this.orders.length > 1
      ? 'additionalBooking_ordered_description_plural'
      : 'additionalBooking_ordered_description';
  }

  /** Zurück zur Zubucher-Übersicht, um weitere Angebote zu buchen. */
  public continueBooking(): void {
    this.navigationService.navigateTo(NavigationCommand.ADDITIONALBOOKINGOVERVIEW);
  }

  /** Führt in die Abonnement-Übersicht des Kunden, in der die neuen Abos nun mit aufgeführt sind. */
  public goToSubscriptions(): void {
    this.navigationService.navigateTo(NavigationCommand.SUBSCRIPTION);
  }
}
